import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Stack,
  Autocomplete,
  Paper,
  Card,
  CardContent,
  Grid,
  Chip,
  Tooltip,
  CircularProgress,
  Divider,
} from "@mui/material";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import EditOutlined from "@mui/icons-material/EditOutlined";
import CheckCircleOutline from "@mui/icons-material/CheckCircleOutline";
import AddCircleOutline from "@mui/icons-material/AddCircleOutline";
import CheckCircle from "@mui/icons-material/CheckCircle";
import Close from "@mui/icons-material/Close";
import { createAxiosInstance } from "../../../api";
import { useDispatch, useSelector } from "react-redux";
import { getMyProfile } from "../../../store/setProfile";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
// @ts-ignore - SVG import
import errorIcon from "../../../assets/error-icon.svg";

const examBoards = ["AQA", "OCR", "Edexcel", "WJEC", "CCEA", "Other"];
const levels = [
  "GCSE",
  "A-Level",
  "Scottish Highers",
  "IB (International Baccalaureate)",
  "BTEC",
  "Primary",
  "Secondary",
  "University",
  "Adult Learner",
  "Other",
];

type Row = {
  _id?: string;
  subject: string;
  level: string;
  board?: string;
  price?: string;
  saved?: boolean;
  isRowDisabled?: boolean;
  editing?: boolean;
};

export default function Subjects({ onDirtyChange, onSave }: any) {
  const dispatch = useDispatch();
  const profileUser = useSelector((state: any) => state.profile?.user);
  const { status: profileStatus } = profileUser || {};
  const { status: accountStatus } = useSelector((state: any) => state.main) || {};

  let userId: string;
  try {
    const persistedData = localStorage.getItem("persist:root");
    const userData = JSON.parse(persistedData || "{}");
    if (userData.userId && typeof userData.userId === "string") {
      userData.userId = userData.userId.replace(/\\/g, "");
    }
    userId = userData?.userId?.replace(/"/g, "") || "";
  } catch (error) {
    console.error("Error parsing localStorage data:", error);
    userId = "";
  }

  const [subjects, setSubjects] = useState<string[]>([]);
  const [filteredSubjects, setFilteredSubjects] = useState<string[]>([]);
  const [savedRows, setSavedRows] = useState<Row[]>([]);
  const [draftRow, setDraftRow] = useState<Row>({
    subject: "",
    level: "",
    board: "",
    price: "",
    saved: false,
  });
  const [editingRow, setEditingRow] = useState<string | null>(null);
  const [editDataMap, setEditDataMap] = useState<Record<string, Row>>({});
  const [loading, setLoading] = useState(false);
  const [isFetchingSubjects, setIsFetchingSubjects] = useState(false);

  const fetchSavedRows = useCallback(async () => {
    if (!userId) return;
    setIsFetchingSubjects(true);
    try {
      const res = await createAxiosInstance().get(
        `/api/subjects/user/${userId}`
      );
      const saved = (res.data || []).map((s: any) => ({
        _id: s._id,
        subject: s.subject || "",
        level: s.level || "",
        board: s.board || "",
        price: String(s.price || ""),
        saved: true,
        isRowDisabled: true,
      }));
      setSavedRows(saved);
    } catch (err) {
      console.error("Failed to fetch subjects:", err);
    } finally {
      setIsFetchingSubjects(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchSavedRows();
    createAxiosInstance()
      .get("/api/subjects")
      .then((res) => {
        const subs = (res.data?.subjects || []).map((s: any) => s.name || s.subject || "");
        setSubjects(subs);
        setFilteredSubjects(subs);
      })
      .catch((err) => console.error("Failed to fetch subject list:", err));
  }, [fetchSavedRows]);

  const handleSaveNew = async () => {
    if (!draftRow.subject?.trim() || !draftRow.price?.trim()) {
      toast.error("Subject and price are required!");
      return;
    }

    const isProfileLive = profileStatus === 1 && accountStatus === 1;
    if (isProfileLive) {
      const confirm = await Swal.fire({
        title: "Save Subject",
        text: "This will move your profile to draft status. Continue?",
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#712d94",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes",
        cancelButtonText: "Cancel",
      });
      if (!confirm.isConfirmed) return;
    }

    setLoading(true);
    try {
      await createAxiosInstance().post(`/api/subjects/save/${userId}`, {
        subjects: [
          {
            subject: draftRow.subject,
            level: draftRow.level || "",
            board: draftRow.board || "",
            price: draftRow.price,
          },
        ],
      });
      toast.success("Subject saved successfully.");
      (dispatch as any)(getMyProfile());
      setDraftRow({ subject: "", level: "", board: "", price: "", saved: false });
      fetchSavedRows();
      onDirtyChange?.(false);
    } catch (err: any) {
      Swal.fire({
        title: "Error",
        text: err?.response?.data?.message || "Failed to save subject.",
        imageUrl: errorIcon,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (rowId: string, updatedData: Row) => {
    if (!updatedData.subject?.trim() || !updatedData.price?.trim()) {
      toast.error("Subject and price are required!");
      return;
    }

    const isProfileLive = profileStatus === 1 && accountStatus === 1;
    if (isProfileLive) {
      const confirm = await Swal.fire({
        title: "Update Subject",
        text: "This will move your profile to draft status. Continue?",
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#712d94",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes",
        cancelButtonText: "Cancel",
      });
      if (!confirm.isConfirmed) return;
    }

    setLoading(true);
    try {
      await createAxiosInstance().put(
        `/api/subjects/update/${userId}/${rowId}`,
        {
          subject: updatedData.subject,
          level: updatedData.level || "",
          board: updatedData.board || "",
          price: updatedData.price,
        }
      );
      toast.success("Subject updated successfully.");
      (dispatch as any)(getMyProfile());
      setEditingRow(null);
      fetchSavedRows();
      onDirtyChange?.(false);
    } catch (err: any) {
      Swal.fire({
        title: "Error",
        text: err?.response?.data?.message || "Failed to update subject.",
        imageUrl: errorIcon,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (rowId: string, subjectName: string) => {
    const confirm = await Swal.fire({
      title: "Delete Subject",
      text: `Are you sure you want to delete "${subjectName}"?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#712d94",
      cancelButtonColor: "#d33",
    });
    if (!confirm.isConfirmed) return;

    setLoading(true);
    try {
      await createAxiosInstance().delete(`/api/subjects/delete/${userId}/${rowId}`);
      toast.success("Subject deleted successfully.");
      (dispatch as any)(getMyProfile());
      fetchSavedRows();
      onDirtyChange?.(false);
    } catch (err: any) {
      Swal.fire({
        title: "Error",
        text: "Failed to delete subject.",
        imageUrl: errorIcon,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubjectInput = (value: string) => {
    setDraftRow({ ...draftRow, subject: value });
    if (value.length > 0) {
      const filtered = subjects.filter((s) => s.toLowerCase().includes(value.toLowerCase()));
      setFilteredSubjects(filtered);
    } else {
      setFilteredSubjects(subjects);
      setDraftRow({ ...draftRow, subject: "", level: "" });
    }
  };

  (Subjects as any).valid = savedRows.length > 0;
  (Subjects as any).save = async () => {
    if (draftRow.subject && draftRow.price) {
      await handleSaveNew();
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>
        What subjects can you teach?
      </Typography>
      <Typography variant="body2" sx={{ mb: 4, color: "#6b7280" }}>
        (Subject and price are required)
      </Typography>

      {/* Add New Subject Card */}
      <Card
        elevation={0}
        sx={{
          border: "1px solid #e9e9ec",
          borderRadius: 2,
          mb: 3,
          backgroundColor: "#fafafa",
        }}
      >
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <AddCircleOutline color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Add New Subject
            </Typography>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <Autocomplete
                freeSolo
                options={filteredSubjects}
                value={draftRow.subject}
                onInputChange={(_, val) => handleSubjectInput(val)}
                size="small"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Subject *"
                    placeholder="Select or type"
                    error={!draftRow.subject && draftRow.price ? true : false}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={2.5}>
              <Autocomplete
                freeSolo
                options={levels}
                value={draftRow.level}
                onInputChange={(_, val) => setDraftRow({ ...draftRow, level: val || "" })}
                size="small"
                renderInput={(params) => (
                  <TextField {...params} label="Level" placeholder="Select or type" />
                )}
              />
            </Grid>
            <Grid item xs={12} md={2.5}>
              <Autocomplete
                freeSolo
                options={examBoards}
                value={draftRow.board || ""}
                onInputChange={(_, val) => setDraftRow({ ...draftRow, board: val || "" })}
                size="small"
                renderInput={(params) => (
                  <TextField {...params} label="Exam Board" placeholder="Optional" />
                )}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                size="small"
                label="Price (£/hr) *"
                value={draftRow.price || ""}
                onChange={(e) => setDraftRow({ ...draftRow, price: e.target.value })}
                placeholder="0.00"
                type="number"
                error={!draftRow.price && draftRow.subject ? true : false}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant="contained"
                onClick={handleSaveNew}
                disabled={loading || !draftRow.subject || !draftRow.price}
                sx={{ height: "40px" }}
              >
                {loading ? <CircularProgress size={20} /> : "Add Subject"}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Saved Subjects */}
      <Box>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Your Subjects ({savedRows.length})
        </Typography>

        {isFetchingSubjects ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : savedRows.length === 0 ? (
          <Card
            elevation={0}
            sx={{
              border: "1px solid #e9e9ec",
              borderRadius: 2,
              p: 4,
              textAlign: "center",
              backgroundColor: "#fafafa",
            }}
          >
            <Typography variant="body1" color="text.secondary">
              No subjects added yet. Use the form above to add your first subject.
            </Typography>
          </Card>
        ) : (
          <Grid container spacing={2} sx={{ width: "100%" }}>
            {savedRows.map((row) => {
              const isEditing = editingRow === row._id;
              const editData = editDataMap[row._id!] || row;

              return (
                <Grid item xs={12} key={row._id}>
                  <Card
                    elevation={0}
                    sx={{
                      width: "100%",
                      border: "1px solid #e9e9ec",
                      borderRadius: 2,
                      "&:hover": {
                        borderColor: "#712d94",
                        boxShadow: "0 2px 8px rgba(113, 45, 148, 0.1)",
                      },
                      transition: "all 0.2s ease",
                    }}
                  >
                    <CardContent sx={{ width: "100%", p: 2.5 }}>
                      {isEditing ? (
                        <Grid container spacing={2} alignItems="center" sx={{ width: "100%" }}>
                          <Grid item xs={12} sm={12} md={3.5}>
                            <Autocomplete
                              freeSolo
                              options={filteredSubjects}
                              value={editData.subject}
                              onInputChange={(_, val) =>
                                setEditDataMap({ ...editDataMap, [row._id!]: { ...editData, subject: val || "" } })
                              }
                              size="small"
                              fullWidth
                              renderInput={(params) => (
                                <TextField {...params} label="Subject *" fullWidth />
                              )}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={2.5}>
                            <Autocomplete
                              freeSolo
                              options={levels}
                              value={editData.level}
                              onInputChange={(_, val) =>
                                setEditDataMap({ ...editDataMap, [row._id!]: { ...editData, level: val || "" } })
                              }
                              size="small"
                              fullWidth
                              renderInput={(params) => (
                                <TextField {...params} label="Level" fullWidth />
                              )}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={2.5}>
                            <Autocomplete
                              freeSolo
                              options={examBoards}
                              value={editData.board || ""}
                              onInputChange={(_, val) =>
                                setEditDataMap({ ...editDataMap, [row._id!]: { ...editData, board: val || "" } })
                              }
                              size="small"
                              fullWidth
                              renderInput={(params) => (
                                <TextField {...params} label="Exam Board" fullWidth />
                              )}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={2}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Price (£/hr) *"
                              value={editData.price || ""}
                              onChange={(e) =>
                                setEditDataMap({ ...editDataMap, [row._id!]: { ...editData, price: e.target.value } })
                              }
                              type="number"
                            />
                          </Grid>
                          <Grid item xs={12} sm={6} md={1.5}>
                            <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                              <Tooltip title="Save changes">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleUpdate(row._id!, editData)}
                                  disabled={loading || !editData.subject || !editData.price}
                                  sx={{
                                    border: "1px solid #712d94",
                                    backgroundColor: "rgba(113, 45, 148, 0.08)",
                                    "&:hover": {
                                      backgroundColor: "rgba(113, 45, 148, 0.15)",
                                    },
                                  }}
                                >
                                  {loading ? (
                                    <CircularProgress size={18} />
                                  ) : (
                                    <CheckCircle fontSize="small" />
                                  )}
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Cancel">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setEditingRow(null);
                                    const newMap = { ...editDataMap };
                                    delete newMap[row._id!];
                                    setEditDataMap(newMap);
                                  }}
                                  disabled={loading}
                                  sx={{
                                    border: "1px solid #e0e0e0",
                                    "&:hover": {
                                      borderColor: "#d32f2f",
                                      color: "#d32f2f",
                                    },
                                  }}
                                >
                                  <Close fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </Grid>
                        </Grid>
                      ) : (
                        <Box>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "flex-start",
                              mb: 2,
                            }}
                          >
                            <Box>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                  {row.subject}
                                </Typography>
                                <Chip
                                  icon={<CheckCircleOutline fontSize="small" />}
                                  label="Saved"
                                  color="success"
                                  size="small"
                                />
                              </Box>
                              <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1 }}>
                                {row.level && (
                                  <Chip label={`Level: ${row.level}`} size="small" variant="outlined" />
                                )}
                                {row.board && (
                                  <Chip label={`Board: ${row.board}`} size="small" variant="outlined" />
                                )}
                                <Chip
                                  label={`£${row.price}/hr`}
                                  size="small"
                                  color="primary"
                                  sx={{ fontWeight: 600 }}
                                />
                              </Stack>
                            </Box>
                            <Stack direction="row" spacing={1}>
                              <Tooltip title="Edit">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setEditingRow(row._id!);
                                    setEditDataMap({ ...editDataMap, [row._id!]: { ...row } });
                                  }}
                                  sx={{ border: "1px solid #e0e0e0" }}
                                >
                                  <EditOutlined fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete">
                                <IconButton
                                  size="small"
                                  onClick={() => handleDelete(row._id!, row.subject)}
                                  sx={{ border: "1px solid #e0e0e0" }}
                                  color="error"
                                >
                                  <DeleteOutline fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </Box>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Box>
    </Box>
  );
}
